import './medFormInOrderSet.style.css';
import { useForm } from 'react-hook-form';
import { useGetAllCatalogMedsOptions } from './hooks/useGetAllCatalogMedsOptions';
import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { omit } from 'lodash';
import TaperInstruction from './components/TaperInstruction';
import {
  MedicationChangeTypeLabel,
  MedicationSectionBreakLabel,
} from '../../../pages/MedicationProfile/constants';
import { tranformTaperFormat } from '../../../../lib/transferObjects/Medication';
import { CareMapprTextarea } from '../../Inputs/textarea';
import { useMedicationProfileDirty } from '../../../pages/MedicationProfile/context/MedicationProfileDirtyProvider';

interface MedFormInOrderSetProps {
  data: any;
  onCancelEdit: () => void;
  onOrderSetModeChange: (values: any) => void;
  sectionOptions?: { value: string; label: string }[];
  showFurtherInform?: boolean;
  showSection?: boolean;
  requiredSection?: boolean;
  readOnly?: boolean;
}
export const MedFormInOrderSet = ({
  data,
  onCancelEdit,
  onOrderSetModeChange,
  sectionOptions,
  showFurtherInform = true,
  showSection = true,
  requiredSection = false,
  readOnly = false,
}: MedFormInOrderSetProps) => {
  const { postGetAllCatalogMedsOptions, optionsData } =
    useGetAllCatalogMedsOptions();
  const { indicationOptions } = optionsData;
  const { setIsDirty } = useMedicationProfileDirty();
  const [blankTextAreaValues, setBlankTextAreaValues] = useState<
    Record<string, string>
  >({});
  const indicationDescriptionByIndication = useMemo(() => {
    const map = new Map<string, string>();
    const pairs = Array.isArray(data?.indicationOptions)
      ? data.indicationOptions
      : Array.isArray(data?.indications)
        ? data.indications
        : [];
    for (const item of pairs) {
      const indication =
        typeof item?.indication === 'string' ? item.indication.trim() : '';
      const description =
        typeof item?.description === 'string' ? item.description : '';
      if (indication.length === 0) continue;
      map.set(indication, description);
    }
    return map;
  }, [data]);

  const availableIndicationOptions = useMemo<string[]>(() => {
    const opts: string[] = Array.isArray(indicationOptions)
      ? indicationOptions
      : [];
    if (opts.length > 0) return opts;
    const fallback = Array.isArray(data?.indicationOptions)
      ? data.indicationOptions
      : Array.isArray(data?.indications)
        ? data.indications
        : [];
    return fallback
      .map((item: any) => item?.indication)
      .filter(
        (value: any) => typeof value === 'string' && value.trim().length > 0,
      );
  }, [indicationOptions, data]);

  const medChangeTypeOptions = Object.values(MedicationChangeTypeLabel).map(
    (item) => ({
      value: item,
      label: item,
    }),
  );
  const medSectionBreakOptions = Object.values(MedicationSectionBreakLabel).map(
    (item) => ({
      value: item,
      label: item,
    }),
  );

  // Normalize taper headers to objects with a title property
  const normalizeTaperHeaders = (headers: any) => {
    if (!Array.isArray(headers)) return [];
    return headers.map((h: any) =>
      typeof h === 'string' ? { title: h } : { title: h?.title ?? '' },
    );
  };

  const normalizedData = useMemo(() => {
    const headers = data?.regimen?.taper?.taperHeaders;
    const taper = {
      ...(data?.regimen?.taper || {}),
      taperHeaders: normalizeTaperHeaders(headers),
    };
    return {
      ...data,
      regimen: {
        ...(data?.regimen || {}),
        taper,
      },
    };
  }, [data]);

  const medForm = useForm({
    defaultValues: {
      ...normalizedData,
      medSectionBreak: Array.isArray(sectionOptions)
        ? sectionOptions[0].value
        : medSectionBreakOptions[0].value,
    },
  });

  const { watch, reset, formState } = medForm;

  // mark dirty when form changes relative to defaultValues
  useEffect(() => {
    setIsDirty(Boolean(formState.isDirty));
  }, [formState.isDirty, setIsDirty]);

  const medChangeTypeWatchValue = watch('medChangeType');
  const medSectionBreakWatchValue = watch('medSectionBreak');
  const indicationWatchValue = watch('indication');
  const regimenWatchValue = watch('regimen');
  const shouldKeepOtherImportantInformationBlank = Boolean(
    normalizedData?.regimen?.isPrn,
  );

  const renderAutoResizeTextArea = (
    name:
      | 'medName'
      | 'subheading'
      | 'brand'
      | 'indicationDescription'
      | 'furtherInformation'
      | 'furtherDescriptionOfChange',
    controlId: string,
    placeholder: string,
    options: {
      keepBlank?: boolean;
    } = {},
  ) => {
    const { keepBlank = false } = options;
    return (
      <CareMapprTextarea
        autoResize
        placeholder={placeholder}
        controlId={controlId}
        inputClassName="med-input"
        value={
          keepBlank
            ? String(blankTextAreaValues[controlId] || '')
            : String(watch(name) || '')
        }
        onChange={(e) => {
          if (keepBlank) {
            setBlankTextAreaValues((prev) => ({
              ...prev,
              [controlId]: e.target.value,
            }));
            return;
          }
          medForm.setValue(name, e.target.value, { shouldDirty: true });
        }}
      />
    );
  };

  const safeText = (value: unknown) => {
    if (value === null || value === undefined) return '-';
    const s = String(value);
    return s.trim().length > 0 ? s : '-';
  };

  const renderReadOnlyRegimen = () => {
    const regimen: any = regimenWatchValue || {};
    const isDoseSchedule = Boolean(regimen?.isDoseSchedule);
    const isPrn = Boolean(regimen?.isPrn);
    const isTaper = Boolean(regimen?.isTaper);
    const mode = isDoseSchedule
      ? 'Schedule'
      : isPrn
        ? 'PRN/other'
        : isTaper
          ? 'Taper'
          : '-';

    const dose: any = regimen?.doseSchedule || {};
    const prnNote = regimen?.prn?.prnNote;
    const taper: any = regimen?.taper || {};
    const taperHeaders: any[] = Array.isArray(taper?.taperHeaders)
      ? taper.taperHeaders
      : [];
    const taperSchedule: any[] = Array.isArray(taper?.taperSchedule)
      ? taper.taperSchedule
      : [];
    const headerTitles = taperHeaders.map((h) => safeText(h?.title ?? h));

    const scheduleLines: Array<{ label: string; note: unknown }> = [
      {
        label: 'Breakfast 7-9 AM',
        note: dose?.breakfastNote,
        enabled: Boolean(dose?.isBreakfast),
      } as any,
      {
        label: 'Lunch 11-1 PM',
        note: dose?.lunchNote,
        enabled: Boolean(dose?.isLunch),
      } as any,
      {
        label: 'Dinner 4-6PM',
        note: dose?.dinnerNote,
        enabled: Boolean(dose?.isDinner),
      } as any,
      {
        label: 'Bedtime 8-10PM',
        note: dose?.bedNote,
        enabled: Boolean(dose?.isBed),
      } as any,
    ].filter((l: any) => l.enabled);

    return (
      <div className="use-description-container">
        <div
          className="filterItem"
          style={{ marginLeft: '15px', marginRight: '15px' }}
        >
          <label className="txtDropdownLabel">
            How should patient take this?
          </label>
          <div className="med-input">{mode}</div>
        </div>

        {isDoseSchedule ? (
          <div style={{ marginLeft: '15px', marginRight: '15px' }}>
            {scheduleLines.length > 0 ? (
              scheduleLines.map((l: any, idx: number) => (
                <div key={`${l.label}-${idx}`} className="schedule-container">
                  <span>{l.label}</span>
                  <div className="med-input" style={{ whiteSpace: 'pre-wrap' }}>
                    {safeText(l.note)}
                  </div>
                </div>
              ))
            ) : (
              <div className="med-input">-</div>
            )}
          </div>
        ) : null}

        {isPrn ? (
          <div
            className="filterItem"
            style={{ marginLeft: '15px', marginRight: '15px' }}
          >
            <label className="txtDropdownLabel">Describe further</label>
            <div className="med-input" style={{ whiteSpace: 'pre-wrap' }}>
              {safeText(prnNote)}
            </div>
          </div>
        ) : null}

        {isTaper ? (
          <div style={{ marginLeft: '15px', marginRight: '15px' }}>
            <div className="my-3">
              <label className="txtDropdownLabel">Dosing schedule</label>
              <div className="med-input" style={{ whiteSpace: 'pre-wrap' }}>
                {safeText(taper?.dosingSchedule)}
              </div>
            </div>
            <div className="my-3">
              <label className="txtDropdownLabel">Taper title</label>
              <div className="med-input" style={{ whiteSpace: 'pre-wrap' }}>
                {safeText(taper?.taperTitle)}
              </div>
            </div>
            {headerTitles.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      {headerTitles.map((t, idx) => (
                        <th key={`${t}-${idx}`}>{t}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {taperSchedule.length > 0 ? (
                      taperSchedule.map((row: any, rIdx: number) => {
                        const values: any[] = Array.isArray(row?.values)
                          ? row.values
                          : [];
                        return (
                          <tr key={`taper-row-${rIdx}`}>
                            {headerTitles.map((_, cIdx) => (
                              <td key={`taper-cell-${rIdx}-${cIdx}`}>
                                {safeText(values[cIdx])}
                              </td>
                            ))}
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={headerTitles.length}>-</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="med-input">-</div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  useEffect(() => {
    const current = medForm.getValues('indication');
    if (!current && availableIndicationOptions.length > 0) {
      const indication = availableIndicationOptions[0];
      medForm.setValue('indication', indication, {
        shouldDirty: false,
      });
      const mappedDescription =
        indicationDescriptionByIndication.get(indication) ?? '';
      if (mappedDescription) {
        medForm.setValue('indicationDescription', mappedDescription, {
          shouldDirty: false,
        });
      }
    } else if (current) {
      const mappedDescription =
        indicationDescriptionByIndication.get(current) ?? '';
      if (mappedDescription) {
        medForm.setValue('indicationDescription', mappedDescription, {
          shouldDirty: false,
        });
      }
    }
  }, [availableIndicationOptions, indicationDescriptionByIndication, medForm]);

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name !== 'indication') return;
      const indication = medForm.getValues('indication');
      if (!indication) return;
      const mappedDescription =
        indicationDescriptionByIndication.get(indication) ?? '';
      if (!mappedDescription) return;
      medForm.setValue('indicationDescription', mappedDescription, {
        shouldDirty: false,
      });
    });
    return () => subscription.unsubscribe();
  }, [indicationDescriptionByIndication, medForm, watch]);

  const handleMedFormSubmit = () => {
    const formValues = medForm.getValues();
    const regimen = {
      ...formValues?.regimen,
      // ensure booleans instead of relying on 'on'
      isDoseSchedule: Boolean(formValues?.regimen?.isDoseSchedule),
      isPrn: Boolean(formValues?.regimen?.isPrn),
      isTaper: Boolean(formValues?.regimen?.isTaper),
      taper: tranformTaperFormat(formValues?.regimen?.taper),
    };

    const payload = omit(formValues, ['details', 'id']);
    if (!!regimen) {
      payload.regimen = regimen;
    }

    onOrderSetModeChange(payload);
    // reset to the saved payload so the UI reflects persisted state
    medForm.reset(payload);
    // cleared dirty after reset
    setIsDirty(false);
    onCancelEdit && onCancelEdit();
  };

  useEffect(() => {
    if (!optionsData) {
      postGetAllCatalogMedsOptions();
    }
  }, []);

  return (
    <>
      <form className="med-form-container">
        <div className="three-parts-row">
          <div className="three-parts-column">
            <div className="filterItem">
              <label className="txtDropdownLabel">
                Medication name and strength
              </label>
              <div className="divSelect">
                <div>
                  {readOnly ? (
                    <div className="med-input">
                      {safeText(watch('medName'))}
                    </div>
                  ) : (
                    renderAutoResizeTextArea(
                      'medName',
                      'med-name-and-strength',
                      'Start typing',
                    )
                  )}
                </div>
              </div>
            </div>

            <div className="filterItem">
              <label className="txtDropdownLabel">Subheading</label>
              <div>
                {readOnly ? (
                  <div className="med-input">
                    {safeText(watch('subheading'))}
                  </div>
                ) : (
                  renderAutoResizeTextArea('subheading', 'med-subheading', '')
                )}
              </div>
            </div>

            <div className="filterItem">
              <label className="txtDropdownLabel">Brand</label>
              <div>
                {readOnly ? (
                  <div className="med-input">{safeText(watch('brand'))}</div>
                ) : (
                  renderAutoResizeTextArea('brand', 'med-brand', 'Start typing')
                )}
              </div>
            </div>

            {showSection ? (
              <div className="filterItem">
                <label className="txtDropdownLabel">Add to section</label>
                <div>
                  <div className="divSelect">
                    {readOnly ? (
                      <div className="med-input">
                        {safeText(medSectionBreakWatchValue)}
                      </div>
                    ) : (
                      <Select
                        options={
                          Array.isArray(sectionOptions)
                            ? sectionOptions
                            : medSectionBreakOptions
                        }
                        onChange={(selectedOptions) => {
                          // @ts-expect-error
                          const medSectionBreak = selectedOptions.value;
                          medForm.setValue(`medSectionBreak`, medSectionBreak);
                        }}
                        isMulti={false}
                        value={
                          (Array.isArray(sectionOptions)
                            ? sectionOptions
                            : medSectionBreakOptions
                          ).find(
                            (opt) => opt.value === medSectionBreakWatchValue,
                          ) || ''
                        }
                        placeholder="Select.."
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div className="three-parts-column">
            <div className="filterItem">
              <label className="txtDropdownLabel">Indication</label>
              <div>
                <div className="divSelect">
                  {readOnly ? (
                    <div className="med-input">
                      {safeText(indicationWatchValue)}
                    </div>
                  ) : (
                    <Select
                      onChange={(selectedOptions) => {
                        const indication = selectedOptions?.value;
                        if (!indication) return;
                        medForm.setValue('indication', indication, {
                          shouldDirty: true,
                        });
                        const mappedDescription =
                          indicationDescriptionByIndication.get(indication) ??
                          '';
                        if (mappedDescription) {
                          medForm.setValue(
                            'indicationDescription',
                            mappedDescription,
                            { shouldDirty: true },
                          );
                        }
                      }}
                      placeholder={'Select'}
                      options={availableIndicationOptions.map(
                        (item: string) => {
                          return {
                            value: item,
                            label: item,
                          };
                        },
                      )}
                      isMulti={false}
                      value={
                        indicationWatchValue
                          ? {
                              value: indicationWatchValue,
                              label: indicationWatchValue,
                            }
                          : null
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="filterItem">
              <label className="txtDropdownLabel">Indication description</label>
              <div>
                {readOnly ? (
                  <div className="med-input">
                    {safeText(watch('indicationDescription'))}
                  </div>
                ) : (
                  renderAutoResizeTextArea(
                    'indicationDescription',
                    'indication-description',
                    'Start typing...',
                  )
                )}
              </div>
            </div>

            <div className="filterItem">
              {readOnly ? (
                renderReadOnlyRegimen()
              ) : (
                <TaperInstruction
                  form={medForm}
                  furtherInform={
                    showFurtherInform ? (
                      <div
                        className="med-form-container-row"
                        style={{ minWidth: '240px' }}
                      >
                        <div className="filterItem">
                          <label className="txtDropdownLabel">
                            Describe further
                          </label>
                          <div>
                            {renderAutoResizeTextArea(
                              'furtherInformation',
                              'describe-further',
                              'Start typing...',
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null
                  }
                />
              )}
            </div>
          </div>
          <div className="three-parts-column">
            <div className="filterItem">
              <label className="txtDropdownLabel">
                Other important information
              </label>
              <div>
                {readOnly ? (
                  <div
                    className="med-input"
                    style={{ whiteSpace: 'pre-wrap', height: '40px' }}
                  >
                    {shouldKeepOtherImportantInformationBlank
                      ? '-'
                      : safeText(watch('furtherInformation'))}
                  </div>
                ) : (
                  renderAutoResizeTextArea(
                    'furtherInformation',
                    'other-important-information',
                    'Start typing...',
                    {
                      keepBlank: shouldKeepOtherImportantInformationBlank,
                    },
                  )
                )}
              </div>
            </div>

            <div className="filterItem">
              <label className="txtDropdownLabel">Change type</label>
              <div>
                <div className="divSelect">
                  {readOnly ? (
                    <div className="med-input">
                      {safeText(medChangeTypeWatchValue)}
                    </div>
                  ) : (
                    <Select
                      options={medChangeTypeOptions}
                      onChange={(selectedOption) => {
                        // @ts-expect-error
                        const changeType = selectedOption?.value;
                        if (changeType)
                          medForm.setValue(`medChangeType`, changeType);
                      }}
                      isMulti={false}
                      value={
                        medChangeTypeOptions.find(
                          (opt) => opt.value === medChangeTypeWatchValue,
                        ) || ''
                      }
                      placeholder="Add change type"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="filterItem">
              <label className="txtDropdownLabel">
                Further description of change
              </label>
              <div>
                {readOnly ? (
                  <div className="med-input">
                    {safeText(watch('furtherDescriptionOfChange'))}
                  </div>
                ) : (
                  renderAutoResizeTextArea(
                    'furtherDescriptionOfChange',
                    'further-description-of-change',
                    'Start typing...',
                  )
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            className="btn"
            type="button"
            style={{ width: '150px', fontWeight: 'bold' }}
            onClick={() => {
              reset();
              onCancelEdit && onCancelEdit();
            }}
          >
            {readOnly ? 'Close' : 'Cancel & close'}
          </button>
          {!readOnly ? (
            <button
              className="btn btnPrimary"
              type="button"
              disabled={requiredSection && !medSectionBreakWatchValue}
              style={{ width: '150px' }}
              onClick={handleMedFormSubmit}
            >
              Save
            </button>
          ) : null}
        </div>
      </form>
    </>
  );
};
